import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Baby,
  ChevronDown,
  Footprints,
  Landmark,
  LocateFixed,
  Mountain,
  PartyPopper,
  UserRound,
  UtensilsCrossed,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { BLUR_OPTIONS, SUBJECTS, recommend, type BlurPreference } from '@/domain/recommend'
import { LensPicker } from '@/components/LensPicker'
import type { Lens } from '@/domain/lens'
import { lightingFor } from '@/domain/lighting'
import { suggestLight } from '@/domain/autolight'
import { formatShutter } from '@/domain/dials'
import { locationPreGranted, readSky, type SkyReading } from '@/services/skyConditions'
import type { CameraState } from '@/domain/types'

type SkyState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; reading: SkyReading }
  | { status: 'error' }

/** Which glyph a subject gets is presentation, so it lives here, not in domain. */
const SUBJECT_ICONS: Record<string, LucideIcon> = {
  buildings: Landmark,
  landscape: Mountain,
  street: Footprints,
  food: UtensilsCrossed,
  action: Zap,
  kids: Baby,
  portrait: UserRound,
  indoors: PartyPopper,
}

/**
 * The split view. Top half: the settings to put on the camera, always
 * visible, updating live. Bottom half: a swipeable three-page deck —
 * subject, blur, light — so answering never hides the answer.
 */
export function Recommender({
  lens,
  onLensChange,
  onOpenInDials,
}: {
  lens: Lens
  onLensChange: (l: Lens) => void
  onOpenInDials: (s: CameraState) => void
}) {
  const [subjectId, setSubjectId] = useState(SUBJECTS[0].id)
  const [blur, setBlur] = useState<BlurPreference>(SUBJECTS[0].defaultBlur)
  const [blurChosen, setBlurChosen] = useState(false)
  const [lightId, setLightId] = useState(lightingFor(SUBJECTS[0].id).defaultId)
  // Once the user picks a light by hand, detection stops overriding it.
  const [lightChosen, setLightChosen] = useState(false)
  const [tripod, setTripod] = useState(false)
  const [page, setPage] = useState(0)
  // One row of detail open at a time; everything else stays one line.
  const [expanded, setExpanded] = useState<string | null>(null)
  const [skyState, setSkyState] = useState<SkyState>({ status: 'idle' })

  const deckRef = useRef<HTMLDivElement>(null)

  const lighting = lightingFor(subjectId)
  const light =
    lighting.options.find((o) => o.id === lightId) ??
    lighting.options.find((o) => o.id === lighting.defaultId)!

  const suggestion =
    skyState.status === 'ready' ? suggestLight(subjectId, skyState.reading.sky) : null

  const detectSky = async () => {
    setSkyState({ status: 'loading' })
    try {
      const reading = await readSky()
      setSkyState({ status: 'ready', reading })
    } catch {
      setSkyState({ status: 'error' })
    }
  }

  // If location was granted on a previous visit, read the sky silently —
  // no prompt fires for an already-granted permission.
  useEffect(() => {
    let cancelled = false
    locationPreGranted().then((granted) => {
      if (granted && !cancelled) void detectSky()
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Detection sets the pre-selected light until the user picks one by hand.
  useEffect(() => {
    if (suggestion && !lightChosen) setLightId(suggestion.optionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion?.optionId, lightChosen])

  const rec = useMemo(
    () => recommend({ subjectId, blur, sceneEv100: light.ev, tripod }, lens),
    [subjectId, blur, light.ev, tripod, lens],
  )

  const goTo = (i: number) => {
    const deck = deckRef.current
    if (deck) deck.scrollTo({ left: i * deck.clientWidth, behavior: 'smooth' })
  }

  const onDeckScroll = () => {
    const deck = deckRef.current
    if (!deck) return
    const i = Math.round(deck.scrollLeft / deck.clientWidth)
    if (i !== page) setPage(i)
  }

  const chooseSubject = (id: string) => {
    setSubjectId(id)
    setBlurChosen(false)
    const subject = SUBJECTS.find((s) => s.id === id)
    if (subject) setBlur(subject.defaultBlur)
    // The light list changes with the subject. Prefer the detected sky,
    // then the previous answer if the new subject offers it, then default.
    setLightChosen(false)
    const next = lightingFor(id)
    if (skyState.status === 'ready') {
      setLightId(suggestLight(id, skyState.reading.sky).optionId)
    } else if (!next.options.some((o) => o.id === lightId)) {
      setLightId(next.defaultId)
    }
    goTo(1)
  }

  const chooseBlur = (b: BlurPreference) => {
    setBlur(b)
    setBlurChosen(true)
    goTo(2)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ── Top half: the answer, at a glance ────────────────────────────── */}
      <section className="flex min-h-0 flex-[1.15] flex-col border-b border-edge">
        <div className="flex items-center justify-between px-4 pt-2 pb-1">
          <span className="text-[10px] font-bold uppercase tracking-[.2em] text-muted">
            Nikon ZFC · set this
          </span>
          <button
            onClick={() => onOpenInDials(rec.cameraState)}
            className="text-[11px] font-medium text-muted transition-colors hover:text-chrome"
          >
            Learn the dials →
          </button>
        </div>

        <div className="px-4 pb-1.5">
          <LensPicker lens={lens} onChange={onLensChange} />
        </div>

        {/* The numbers */}
        <div className="grid grid-cols-5 gap-1 px-4 pb-2">
          <Stat label="Mode" value={rec.mode} />
          <Stat label="Aperture" value={`f/${rec.aperture}`} />
          <Stat label="ISO" value={String(rec.iso)} />
          <Stat
            label="Shutter"
            value={
              rec.mode === 'A'
                ? `~${formatShutter(rec.shutterSeconds)}`
                : formatShutter(rec.shutterSeconds)
            }
          />
          <Stat label="Zoom" value={`${rec.focalLength}mm`} />
        </div>

        {/* One line per dial. Tap a row for the where and the why. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
          {/* When nothing will work, that fact leads — not the numbers. */}
          {rec.impossible &&
            rec.warnings
              .filter((w) => w.level === 'problem')
              .map((w) => (
                <GlanceRow
                  key={w.title}
                  id={`warn-${w.title}`}
                  expanded={expanded}
                  onToggle={setExpanded}
                  title={w.title}
                  detail={w.detail}
                  tone="red"
                />
              ))}

          <ol className={rec.impossible ? 'opacity-50' : ''}>
            {rec.instructions.map((step) => {
              const [name, location] = splitControl(step.control)
              return (
                <GlanceRow
                  key={step.id}
                  id={`step-${step.id}`}
                  expanded={expanded}
                  onToggle={setExpanded}
                  title={name}
                  chip={step.setTo}
                  chipMuted={step.inert}
                  detail={location ? `${location}. ${step.why}` : step.why}
                />
              )
            })}
          </ol>

          {rec.warnings
            .filter((w) => !(rec.impossible && w.level === 'problem'))
            .map((w) => (
              <GlanceRow
                key={w.title}
                id={`warn-${w.title}`}
                expanded={expanded}
                onToggle={setExpanded}
                title={w.title}
                detail={w.detail}
                tone={w.level === 'problem' ? 'red' : w.level === 'caution' ? 'amber' : undefined}
              />
            ))}

          <GlanceRow
            id="tip"
            expanded={expanded}
            onToggle={setExpanded}
            title="Do this too"
            detail={rec.tip}
            tone="mint"
          />
        </div>
      </section>

      {/* ── Bottom half: the questions ───────────────────────────────────── */}
      <section className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center gap-1 px-4 pt-2.5 pb-1.5">
          {['Shooting', 'Blur', lighting.question.length > 22 ? 'Light' : lighting.question].map(
            (label, i) => (
              <button
                key={label}
                onClick={() => goTo(i)}
                className={`rounded-lg px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${
                  page === i ? 'bg-edge text-chrome' : 'text-muted hover:text-chrome'
                }`}
              >
                {i + 1} · {i === 0 ? 'Shooting' : i === 1 ? 'Blur' : 'Light'}
              </button>
            ),
          )}
          <div className="ml-auto flex gap-1" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  page === i ? 'bg-nikon-red' : 'bg-edge'
                }`}
              />
            ))}
          </div>
        </div>

        <div
          ref={deckRef}
          onScroll={onDeckScroll}
          className="no-scrollbar flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        >
          {/* Page 1 · subject */}
          <DeckPage>
            <PageTitle>What are you shooting?</PageTitle>
            <div className="grid grid-cols-3 gap-1.5">
              {SUBJECTS.map((s) => {
                const Icon = SUBJECT_ICONS[s.id] ?? UserRound
                const active = subjectId === s.id
                return (
                  <button
                    key={s.id}
                    onClick={() => chooseSubject(s.id)}
                    aria-pressed={active}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-1 py-2.5 transition-colors ${
                      active
                        ? 'border-nikon-red bg-nikon-red/10'
                        : 'border-edge bg-panel/60 hover:border-muted/40'
                    }`}
                  >
                    <Icon
                      size={20}
                      strokeWidth={1.75}
                      className={active ? 'text-nikon-red' : 'text-muted'}
                      aria-hidden="true"
                    />
                    <span className="text-center text-[10.5px] font-medium leading-tight">
                      {s.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </DeckPage>

          {/* Page 2 · blur — the glyphs carry it, no explanations */}
          <DeckPage>
            <PageTitle>How much background blur?</PageTitle>
            <div className="flex h-[calc(100%-2.5rem)] flex-col gap-1.5">
              {BLUR_OPTIONS.map((b) => (
                <button
                  key={b.id}
                  onClick={() => chooseBlur(b.id)}
                  aria-pressed={blur === b.id}
                  className={`flex flex-1 items-center gap-4 rounded-xl border px-4 transition-colors ${
                    blur === b.id
                      ? 'border-nikon-red bg-nikon-red/10'
                      : 'border-edge bg-panel/60 hover:border-muted/40'
                  }`}
                >
                  <BlurGlyph kind={b.id} active={blur === b.id} />
                  <span className="text-[14px] font-semibold">{b.label}</span>
                  {blur === b.id && !blurChosen && (
                    <span className="ml-auto rounded bg-edge px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider text-muted">
                      picked for you
                    </span>
                  )}
                </button>
              ))}
            </div>
          </DeckPage>

          {/* Page 3 · light, phrased for the subject */}
          <DeckPage>
            <PageTitle>{lighting.question}</PageTitle>

            {/* Detected conditions: a suggestion, never a lock-in */}
            {skyState.status === 'ready' && suggestion ? (
              <button
                onClick={() => {
                  setLightId(suggestion.optionId)
                  setLightChosen(false)
                }}
                className={`mb-2 flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors ${
                  light.id === suggestion.optionId && !lightChosen
                    ? 'border-mint/50 bg-mint/10'
                    : 'border-edge bg-panel/60 hover:border-muted/40'
                }`}
              >
                <LocateFixed size={14} className="shrink-0 text-mint" aria-hidden="true" />
                <span className="text-[11.5px] leading-snug text-muted">
                  <span className="font-semibold text-chrome">Detected: </span>
                  {suggestion.reason}
                  {light.id === suggestion.optionId && !lightChosen
                    ? ' — using it.'
                    : ' — tap to use.'}
                </span>
              </button>
            ) : (
              <button
                onClick={() => void detectSky()}
                disabled={skyState.status === 'loading'}
                className="mb-2 flex w-full items-center gap-2 rounded-xl border border-edge bg-panel/60 px-3 py-2 text-left transition-colors hover:border-muted/40 disabled:opacity-60"
              >
                <LocateFixed
                  size={14}
                  className={`shrink-0 ${skyState.status === 'loading' ? 'animate-pulse text-amber' : 'text-muted'}`}
                  aria-hidden="true"
                />
                <span className="text-[11.5px] text-muted">
                  {skyState.status === 'loading'
                    ? 'Reading your sky…'
                    : skyState.status === 'error'
                      ? "Couldn't read your sky — pick below instead."
                      : 'Detect conditions — uses your location, the time, and the weather.'}
                </span>
              </button>
            )}

            <div className="grid grid-cols-2 gap-1.5">
              {lighting.options.map((o) => (
                <button
                  key={o.id}
                  onClick={() => {
                    setLightId(o.id)
                    setLightChosen(true)
                  }}
                  aria-pressed={light.id === o.id}
                  className={`rounded-xl px-3 py-2 text-left transition-colors ${
                    light.id === o.id
                      ? 'bg-chrome text-ink'
                      : 'bg-panel text-muted hover:bg-edge hover:text-chrome'
                  }`}
                >
                  <span className="block text-[12.5px] font-semibold">{o.label}</span>
                  <span className="block text-[11px] leading-snug opacity-70">{o.hint}</span>
                </button>
              ))}
            </div>

            <div className="mt-2 grid grid-cols-2 gap-1.5">
              <SteadyOption active={!tripod} onClick={() => setTripod(false)} label="Handheld">
                Camera in your hands
              </SteadyOption>
              <SteadyOption
                active={tripod}
                onClick={() => setTripod(true)}
                label="Resting on something"
              >
                Tripod, table, wall
              </SteadyOption>
            </div>
          </DeckPage>
        </div>
      </section>
    </div>
  )
}

/** "ISO dial — the big numbered dial, top left" -> name + location. */
function splitControl(control: string): [string, string | null] {
  const i = control.indexOf(' — ')
  if (i === -1) return [control, null]
  const location = control.slice(i + 3)
  return [control.slice(0, i), location.charAt(0).toUpperCase() + location.slice(1)]
}

/**
 * A single at-a-glance line: title on the left, value chip on the right.
 * Tapping it reveals the where-it-is and the why — the detail is never
 * needed to act, only to understand.
 */
function GlanceRow({
  id,
  expanded,
  onToggle,
  title,
  chip,
  chipMuted,
  detail,
  tone,
}: {
  id: string
  expanded: string | null
  onToggle: (id: string | null) => void
  title: string
  chip?: string
  chipMuted?: boolean
  detail: string
  tone?: 'red' | 'amber' | 'mint'
}) {
  const open = expanded === id
  const dot =
    tone === 'red'
      ? 'bg-nikon-red'
      : tone === 'amber'
        ? 'bg-amber'
        : tone === 'mint'
          ? 'bg-mint'
          : null

  return (
    <div className="border-b border-edge/50 last:border-b-0">
      <button
        onClick={() => onToggle(open ? null : id)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 py-[7px] text-left"
      >
        {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />}
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-chrome">{title}</span>
        {chip && (
          <span
            className={`shrink-0 rounded-md px-2 py-0.5 text-[12.5px] font-bold tabular-nums ${
              chipMuted ? 'bg-edge/60 text-muted' : 'bg-chrome text-ink'
            }`}
            style={{ fontFamily: 'var(--font-dial)' }}
          >
            {chip}
          </span>
        )}
        <ChevronDown
          size={13}
          strokeWidth={2}
          className={`shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && <p className="pb-2.5 pl-0.5 text-[12px] leading-snug text-muted">{detail}</p>}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-panel px-0.5 py-1.5 text-center">
      <div className="truncate text-[8.5px] font-semibold uppercase tracking-[.08em] text-muted">
        {label}
      </div>
      <div
        className="mt-0.5 truncate text-[14px] font-bold tabular-nums leading-none"
        style={{ fontFamily: 'var(--font-dial)' }}
      >
        {value}
      </div>
    </div>
  )
}

function DeckPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full w-full shrink-0 snap-center overflow-y-auto px-4 pb-4 safe-bottom">
      {children}
    </div>
  )
}

function PageTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-2 text-[13px] font-semibold">{children}</h2>
}

function SteadyOption({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl border px-3 py-2 text-left transition-colors ${
        active ? 'border-nikon-red bg-nikon-red/10' : 'border-edge bg-panel/60 hover:border-muted/40'
      }`}
    >
      <span className="block text-[12.5px] font-semibold">{label}</span>
      <span className="block text-[11px] leading-snug text-muted">{children}</span>
    </button>
  )
}

/** A tiny visual of how much the background dissolves. */
function BlurGlyph({ kind, active }: { kind: BlurPreference; active: boolean }) {
  const blurAmount = kind === 'max' ? 2.6 : kind === 'some' ? 1.2 : 0
  return (
    <svg width="40" height="40" viewBox="0 0 34 34" className="shrink-0" aria-hidden="true">
      <defs>
        <filter id={`bg-${kind}`}>
          <feGaussianBlur stdDeviation={blurAmount} />
        </filter>
      </defs>
      <g filter={`url(#bg-${kind})`} opacity={0.75}>
        <circle cx="9" cy="10" r="4" fill="var(--color-muted)" />
        <circle cx="25" cy="8" r="3" fill="var(--color-muted)" />
        <rect x="2" y="24" width="30" height="6" rx="3" fill="var(--color-muted)" />
      </g>
      <circle
        cx="17"
        cy="19"
        r="7"
        fill={active ? 'var(--color-nikon-red)' : 'var(--color-chrome)'}
      />
    </svg>
  )
}
